﻿import { ContractInfo, PaymentDocument } from "../types";

interface DocumentListProps {
  contract: ContractInfo;
  documents: PaymentDocument[];
  selectedIds: string[];
  nextSelectableIndex: number;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
}

function formatCLP(amount: number): string {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(amount);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

const typeLabels: Record<string, string> = {
  boleta: "Boleta",
  cobro_adicional: "Cobro adicional",
  multa: "Multa",
};

const typeColors: Record<string, string> = {
  boleta: "bg-sky-50 text-sky-700 border-sky-200",
  cobro_adicional: "bg-amber-50 text-amber-700 border-amber-200",
  multa: "bg-red-50 text-red-700 border-red-200",
};

const statusColors: Record<string, string> = {
  pendiente: "bg-amber-50 text-amber-600",
  vencido: "bg-red-50 text-red-600",
  pagado: "bg-emerald-50 text-emerald-600",
};

const statusLabels: Record<string, string> = {
  pendiente: "Pendiente",
  vencido: "Vencido",
  pagado: "Pagado",
};

export default function DocumentList({
  contract,
  documents,
  selectedIds,
  nextSelectableIndex,
  onToggle,
  onSelectAll,
}: DocumentListProps) {
  const allSelected = documents.length > 0 && selectedIds.length === documents.length;

  return (
    <div className="bg-white rounded-2xl border border-sky-100 overflow-hidden">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-6 py-5 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sky-100 text-xs font-medium mb-1">Contrato encontrado</p>
            <h3 className="text-lg font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {contract.ownerName}
            </h3>
            <p className="text-sky-100 text-sm mt-1">{contract.address}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sky-100 text-xs">N° Contrato</p>
            <p className="font-bold text-sm">{contract.contractNumber}</p>
            <p className="text-sky-100 text-xs mt-1">Medidor: {contract.meterNumber || "-"}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {documents.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-3xl"></i>
            </div>
            <h4 className="text-slate-700 font-semibold mb-1">¡Sin deudas pendientes!</h4>
            <p className="text-slate-400 text-sm">No tienes documentos pendientes de pago.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-slate-700">
                {documents.length} documento{documents.length !== 1 ? "s" : ""} pendiente{documents.length !== 1 ? "s" : ""}
              </h4>
              <button
                type="button"
                onClick={onSelectAll}
                className="text-xs font-semibold text-sky-600 hover:text-sky-700 cursor-pointer transition-colors whitespace-nowrap"
              >
                {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Debes pagar en orden: primero las boletas más antiguas y luego las más recientes.
            </p>

            <div className="flex flex-col gap-3">
              {documents.map((doc, index) => {
                const isSelected = selectedIds.includes(doc.id);
                const lockedByOrder = !isSelected && index > nextSelectableIndex;

                return (
                  <div
                    key={doc.id}
                    onClick={() => onToggle(doc.id)}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? "border-sky-400 bg-sky-50"
                        : "border-slate-100 hover:border-sky-200 hover:bg-slate-50"
                    } ${lockedByOrder ? "opacity-70" : ""}`}
                  >
                    <div
                      className={`w-5 h-5 flex items-center justify-center rounded-md border-2 shrink-0 mt-0.5 transition-all ${
                        isSelected ? "bg-sky-500 border-sky-500 text-white" : "border-slate-300"
                      }`}
                    >
                      {isSelected && <i className="ri-check-line text-xs"></i>}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${typeColors[doc.type]}`}>
                          {typeLabels[doc.type]}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[doc.status]}`}>
                          {statusLabels[doc.status]}
                        </span>
                        {lockedByOrder && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            Debe pagar anteriores
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{doc.description}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Período: {doc.period}</p>
                      {doc.consumption > 0 && <p className="text-xs text-slate-400">Consumo: {doc.consumption} m³</p>}
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-xs text-slate-400">
                          <i className="ri-calendar-line mr-1"></i>
                          Vence: {formatDate(doc.dueDate)}
                        </p>
                        <p className="text-xs text-slate-400">ID: {doc.id}</p>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-base font-bold text-slate-800">{formatCLP(doc.amount)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
